import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import styles from './WebsiteLeads.module.css';

const WebsiteLeads = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
  });
  const [dataTableConfig, setDataTableConfig] = useState({
    start: 0,
    length: 25,
    currentPage: 1,
  });
  const [sortField, setSortField] = useState('id');
  const [sortDirection, setSortDirection] = useState('desc');

  useEffect(() => {
    loadWebsiteLeads();
  }, []);

  const loadWebsiteLeads = async () => {
    try {
      setLoading(true);
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const response = await fetch(`${baseUrl}/api/website-leads`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && Array.isArray(data.data)) {
          setLeads(data.data);
        } else {
          throw new Error('Invalid response format');
        }
      } else {
        throw new Error('Failed to load Website leads');
      }
    } catch (error) {
      console.error('Error loading Website leads:', error);
      toast.error('Failed to load Website leads');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setFilters((prev) => ({
      ...prev,
      search: e.target.value,
    }));
    setDataTableConfig((prev) => ({
      ...prev,
      currentPage: 1,
      start: 0,
    }));
  };

  const handlePageSizeChange = (newSize) => {
    setDataTableConfig((prev) => ({
      ...prev,
      length: newSize,
      currentPage: 1,
      start: 0,
    }));
  };

  const handlePageChange = (page) => {
    const start = (page - 1) * dataTableConfig.length;
    setDataTableConfig((prev) => ({
      ...prev,
      currentPage: page,
      start,
    }));
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const filteredLeads = leads
    .filter((lead) => {
      const search = filters.search.toLowerCase();
      if (!search) return true;

      return [
        lead.name,
        lead.organization_name,
        lead.job_title,
        lead.email,
        lead.property_address,
      ]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(search));
    })
    .sort((a, b) => {
      const aValue = a[sortField] || '';
      const bValue = b[sortField] || '';

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }

      const aString = String(aValue).toLowerCase();
      const bString = String(bValue).toLowerCase();
      if (aString < bString) return sortDirection === 'asc' ? -1 : 1;
      if (aString > bString) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

  const totalPages = Math.ceil(filteredLeads.length / dataTableConfig.length);
  const startIndex = dataTableConfig.start;
  const endIndex = Math.min(startIndex + dataTableConfig.length, filteredLeads.length);
  const currentLeads = filteredLeads.slice(startIndex, endIndex);

  const SortIcon = ({ direction, isActive }) => (
    <svg
      width="10"
      height="16"
      viewBox="0 0 10 16"
      style={{ marginLeft: 4, verticalAlign: 'middle' }}
    >
      <g fill={isActive ? '#333' : '#bbb'}>
        {direction === 'asc' ? (
          <polygon points="5,3 8,7 2,7" />
        ) : direction === 'desc' ? (
          <polygon points="5,13 2,9 8,9" />
        ) : (
          <>
            <polygon points="5,3 8,7 2,7" />
            <polygon points="5,13 2,9 8,9" />
          </>
        )}
      </g>
    </svg>
  );

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingSpinner}>
          <div className={styles.spinner}></div>
          <p>Loading Website leads...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Breadcrumbs */}
      <nav className={styles.breadcrumb}>
        <ol>
          <li>
            <a href="/dashboard">Dashboard</a>
          </li>
          <li>
            <a href="/clients">Clients</a>
          </li>
          <li className={styles.active}>Website Leads</li>
        </ol>
      </nav>

      {/* Content Area with Background */}
      <div className={styles.contentArea}>
        <div className={styles.pageTitle}>
          <h1>Website Leads</h1>
          <button
            className={styles.refreshButton}
            onClick={loadWebsiteLeads}
            type="button"
          >
            Refresh
          </button>
        </div>

        {/* Table Controls */}
        <div className={styles.tableControls}>
          <div className={styles.leftControls}>
            <div className={styles.entriesControl}>
              <label>
                Show{' '}
                <select
                  value={dataTableConfig.length}
                  onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
                  className={styles.entriesSelect}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>{' '}
                entries
              </label>
            </div>
            <div className={styles.tableSearch}>
              <label>
                Search:{' '}
                <input
                  type="search"
                  className={styles.searchInput}
                  value={filters.search}
                  onChange={handleSearchChange}
                  placeholder="Search leads..."
                />
              </label>
            </div>
          </div>
        </div>

        {/* Table Wrapper */}
        <div className={styles.tableWrapper}>
          <table className={styles.websiteLeadsTable}>
            <thead>
              <tr>
                <th
                  className="sortable"
                  onClick={() => handleSort('id')}
                  style={{ cursor: 'pointer' }}
                >
                  ID
                  <SortIcon direction={sortField === 'id' ? sortDirection : null} isActive={sortField === 'id'} />
                </th>
                <th
                  className="sortable"
                  onClick={() => handleSort('name')}
                  style={{ cursor: 'pointer' }}
                >
                  Name
                  <SortIcon direction={sortField === 'name' ? sortDirection : null} isActive={sortField === 'name'} />
                </th>
                <th
                  className="sortable"
                  onClick={() => handleSort('organization_name')}
                  style={{ cursor: 'pointer' }}
                >
                  Organization
                  <SortIcon direction={sortField === 'organization_name' ? sortDirection : null} isActive={sortField === 'organization_name'} />
                </th>
                <th
                  className="sortable"
                  onClick={() => handleSort('job_title')}
                  style={{ cursor: 'pointer' }}
                >
                  Job Title
                  <SortIcon direction={sortField === 'job_title' ? sortDirection : null} isActive={sortField === 'job_title'} />
                </th>
                <th
                  className="sortable"
                  onClick={() => handleSort('email')}
                  style={{ cursor: 'pointer' }}
                >
                  Email
                  <SortIcon direction={sortField === 'email' ? sortDirection : null} isActive={sortField === 'email'} />
                </th>
                <th
                  className="sortable"
                  onClick={() => handleSort('property_address')}
                  style={{ cursor: 'pointer' }}
                >
                  Property Address
                  <SortIcon direction={sortField === 'property_address' ? sortDirection : null} isActive={sortField === 'property_address'} />
                </th>
              </tr>
            </thead>
            <tbody>
              {currentLeads.map((lead) => (
                <tr key={lead.id}>
                  <td>{lead.id}</td>
                  <td>{lead.name}</td>
                  <td>{lead.organization_name || '-'}</td>
                  <td>{lead.job_title || '-'}</td>
                  <td>
                    <a href={`mailto:${lead.email}`}>{lead.email}</a>
                  </td>
                  <td>{lead.property_address}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className={styles.paginationWrapper}>
            <div className={styles.paginationInfo}>
              Showing {startIndex + 1} to {endIndex} of {filteredLeads.length} entries
            </div>
            <div className={styles.pagination}>
              <button
                className={`${styles.paginationButton} ${dataTableConfig.currentPage === 1 ? styles.disabled : ''}`}
                onClick={() => handlePageChange(dataTableConfig.currentPage - 1)}
                disabled={dataTableConfig.currentPage === 1}
              >
                Previous
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  className={`${styles.paginationButton} ${dataTableConfig.currentPage === page ? styles.active : ''}`}
                  onClick={() => handlePageChange(page)}
                >
                  {page}
                </button>
              ))}

              <button
                className={`${styles.paginationButton} ${dataTableConfig.currentPage === totalPages ? styles.disabled : ''}`}
                onClick={() => handlePageChange(dataTableConfig.currentPage + 1)}
                disabled={dataTableConfig.currentPage === totalPages}
              >
                Next
              </button>
            </div>
          </div>
        )}

        {leads.length === 0 && !loading && (
          <div className={styles.noData}>
            <p>No Website leads found.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default WebsiteLeads;
